<script setup>
import { ref, watch, onMounted } from 'vue'
import { Icon } from '@iconify/vue2'
import { useDropZone } from '@vueuse/core'

const emit = defineEmits(['onInput'])

const dropZoneRef = ref()
const uploading = ref(false)
const progress = ref(0)
const isHovering = ref(false)

const initProgress = () => {
	progress.value += 10
	if (progress.value >= 100) {
		uploading.value = false
		_interval.value = clearInterval(_interval.value)
	}
}

const _interval = ref()

function setPreloadedState() {
	if (props.files?.length && !uploading.value && progress.value === 0) {
		progress.value = 100
	}
}

function handleRemove() {
	if (_interval.value) {
		clearInterval(_interval.value)
		_interval.value = undefined
	}
	uploading.value = false
	progress.value = 0
	emit('onInput', [])
}

function onDrop(files) {
	progress.value = 0
	emit('onInput', files)
	console.log('🚀 ~ file: NewFileUpload.vue:13 ~ onDrop ~ files:', files)
	uploading.value = true

	//TODO fazer com que tempo de upload seja dinamico, respeitando promisses, etc...
	_interval.value = setInterval(initProgress, props.interval)
}

const { isOverDropZone } = useDropZone(dropZoneRef, onDrop)

const props = defineProps({
	accept: {
		type: String,
		default: '*/*',
	},
	multiple: {
		type: Boolean,
		default: false,
	},
	maxFileSize: {
		type: Number,
		default: 5,
	},
	maxFiles: {
		type: Number,
		default: 1,
	},
	files: {
		type: [FileList, Array],
	},
	interval: {
		type: Number,
		default: 50,
	},
	fileSizeMessage: {
		type: String,
		default: '',
	},
})

onMounted(() => {
	setPreloadedState()
})

watch(
	() => props.files,
	(newFiles) => {
		if (newFiles?.length && !uploading.value && progress.value === 0) {
			progress.value = 100
		}
		if (!newFiles?.length) {
			progress.value = 0
			uploading.value = false
		}
	},
)
</script>

<template>
	<div
		ref="dropZoneRef"
		:class="isOverDropZone || uploading || progress >= 100 ? 'border-solid' : 'border-dashed'"
		class="border border-[#737576] text-[#272728] flex items-center flex-col rounded-lg py-4"
	>
		<div
			v-if="!files?.length"
			class="flex flex-col items-center"
		>
			<Icon
				icon="heroicons-outline:paper-clip"
				class="text-2xl mb-4"
			/>
			<span>
				<label
					class="text-[#6745ED] cursor-pointer"
					for="input"
					>{{ $t('selecione') }}</label
				>
				{{ $t('arrasteOArquivo') }}
			</span>
			<div class="flex gap-1">
				<span v-if="accept != '*/*'">(Formato suportado {{ accept }} ).</span>
				<span v-if="fileSizeMessage">{{ fileSizeMessage }}</span>
			</div>
			<input
				id="input"
				type="file"
				:accept="accept"
				name=""
				class="hidden"
				@input="onDrop($event.target.files)"
			/>
		</div>
		<div
			v-else
			class="w-full px-6 flex flex-col py-4"
			@mouseenter="isHovering = true"
			@mouseleave="isHovering = false"
		>
			<div class="flex justify-between items-center">
				<p class="!m-0 text-base font-bold text-[#272728]">
					{{ files[0].name }}
				</p>
				<div class="flex items-center gap-2">
					<Icon
						v-if="progress >= 100"
						icon="iconamoon:check-bold"
						class="text-xl"
						color="#138600"
					/>
					<span
						v-if="progress >= 100 && isHovering"
						class="cursor-pointer"
						@click="handleRemove"
					>
						<Icon
							icon="octicon:x-12"
							class="text-xl hover:text-[#C60020]"
						/>
					</span>
					<span
						v-if="progress < 100"
						class="cursor-pointer"
						@click="handleRemove"
					>
						<Icon
							icon="octicon:x-12"
							class="text-xl hover:text-[#C60020]"
						/>
					</span>
				</div>
			</div>
			<p class="!m-0 text-[#737576]">
				{{ progress >= 100 ? 'Upload concluído' : 'Fazendo upload...' }}
			</p>
			<div class="w-full h-[8px]">
				<div class="w-full h-full bg-[#B9BABA] rounded-full">
					<div
						class="h-full bg-[#34617E] rounded-full transition-all duration-300"
						:style="{ width: `${progress}%` }"
					/>
				</div>
			</div>
		</div>
	</div>
</template>
